import { useToast } from 'components/shared/Snackbars'
import { GraphQLClient } from 'graphql-request'
import { isArray } from 'lodash-es'
import { useMutation, useQuery } from 'react-query'
import { getApiUrl } from 'utils/environment'
import { storage } from 'utils/localStorage'
import { useAuth } from './auth/hooks'

const QUERY_CACHE_MS = 300000 // 5 minutes

export type ID = string
export type ISO8601DateTime = string

export type ApiError = any

export interface BaseModel {
  id: ID
  createdAt?: ISO8601DateTime
  updatedAt?: ISO8601DateTime
}

export interface JSON {
  [key: string]: unknown
}

// try to use existing token, Authorization header is updated in /auth/hooks.ts
export const api = new GraphQLClient(getApiUrl(), {
  headers: {
    authorization: storage.getValue('token') ?? '',
  },
})

export function useBaseQuery<QueryData, QueryVariables>(
  queryNodeKey,
  query,
  variables,
  options?
) {
  const { isAuthenticated } = useAuth()

  return useQuery(
    queryNodeKey,
    async () => {
      const data = await api.request<QueryData, QueryVariables>(
        query,
        variables
      )
      return isArray(queryNodeKey) ? data[queryNodeKey[0]] : data[queryNodeKey]
    },
    {
      enabled:
        // enables the queries only if variables are initialized and there's an authorization token
        Object.values(variables).every((el) => el !== undefined) &&
        isAuthenticated,
      retry: 2,
      staleTime: QUERY_CACHE_MS,
      ...options,
    }
  )
}

export const getErrorsFromApi = (data: any): string[] | null => {
  const collectedErrors = []
  if (!data) return null
  const collectErrors = (errors: any): string[] => {
    switch (errors.constructor) {
      case String:
        return [errors]
      case Array:
        return errors.flatMap(collectErrors)
      default:
        return []
    }
  }
  Object.keys(data).forEach((key) => {
    const value = data[key]
    if (value && typeof value === 'object') {
      if (value?.errors) {
        collectedErrors.push(...collectErrors(value.errors))
      }
      collectedErrors.push(...(getErrorsFromApi(value) || []))
    }
  }, [])
  return collectedErrors.length > 0 ? collectedErrors.flat() : null
}

export function useBaseMutation<MutationData, ApiError, MutationVariables>(
  mutationFn,
  options
) {
  const { showError } = useToast()

  const onSuccessWrapper = (data, variables, context) => {
    const errors = getErrorsFromApi(data)
    if (errors) {
      errors.forEach((error) => showError(error))
      return
    }

    if (options.onSuccess) {
      options.onSuccess(data, variables, context)
    }
  }

  return useMutation<MutationData, ApiError, MutationVariables>(mutationFn, {
    ...options,
    onSuccess: onSuccessWrapper,
  })
}
